/**
 * Parent-owned directory media cascade helpers.
 * Parents build a `candidates` array; the thumb paints `candidates[0]` or monogram.
 */

import {
  resolveConsumerSquareLogoForTheme,
  type ConsumerSquareLogoTheme,
} from 'pi-kiosk-shared';

export type DirectoryMediaProfile = 'storePhoto' | 'brandMark' | 'monogram';

export interface DirectoryMediaCandidate {
  readonly profile: Exclude<DirectoryMediaProfile, 'monogram'>;
  readonly src: string;
}

function toExcludeSet(
  excludeSrcs: ReadonlySet<string> | readonly string[] | undefined,
): Set<string> {
  if (excludeSrcs === undefined) {
    return new Set();
  }
  if (excludeSrcs instanceof Set) {
    return new Set(excludeSrcs);
  }
  return new Set(excludeSrcs);
}

/**
 * Resolve theme-preferred URL with alternate-regime fallback when excluded.
 * Reuses square-logo resolver (same light/dark preference semantics).
 */
function resolveThemeMediaUrl(
  light: string | null | undefined,
  dark: string | null | undefined,
  theme: ConsumerSquareLogoTheme,
  excluded: Set<string>,
): string | null {
  const preferred = resolveConsumerSquareLogoForTheme(
    { logoUrl: light ?? null, logoUrlDark: dark ?? null },
    theme,
  );
  if (preferred !== null && !excluded.has(preferred)) {
    return preferred;
  }
  const alternateTheme: ConsumerSquareLogoTheme = theme === 'dark' ? 'light' : 'dark';
  const alternate = resolveConsumerSquareLogoForTheme(
    { logoUrl: light ?? null, logoUrlDark: dark ?? null },
    alternateTheme,
  );
  if (alternate !== null && !excluded.has(alternate)) {
    return alternate;
  }
  return null;
}

/** Stores: theme-aware imageUrl → square tenantLogoUrl chip (never wordmark). */
export function buildStoreDirectoryCandidates(input: {
  readonly imageUrl?: string | null;
  readonly imageUrlDark?: string | null;
  readonly tenantLogoUrl?: string | null;
  readonly tenantLogoUrlDark?: string | null;
  readonly theme?: ConsumerSquareLogoTheme;
  readonly excludeSrcs?: ReadonlySet<string> | readonly string[];
}): DirectoryMediaCandidate[] {
  const excluded = toExcludeSet(input.excludeSrcs);
  const theme = input.theme ?? 'light';
  const candidates: DirectoryMediaCandidate[] = [];
  const imageSrc = resolveThemeMediaUrl(
    input.imageUrl,
    input.imageUrlDark,
    theme,
    excluded,
  );
  if (imageSrc !== null) {
    candidates.push({ profile: 'storePhoto', src: imageSrc });
  }
  const tenantLogoSrc = resolveThemeMediaUrl(
    input.tenantLogoUrl,
    input.tenantLogoUrlDark,
    theme,
    excluded,
  );
  if (tenantLogoSrc !== null) {
    candidates.push({ profile: 'brandMark', src: tenantLogoSrc });
  }
  return candidates;
}

/** Orgs / memberships: theme-aware square logoUrl only → monogram fallback. */
export function buildOrgDirectoryCandidates(input: {
  readonly logoUrl?: string | null;
  readonly logoUrlDark?: string | null;
  readonly theme?: ConsumerSquareLogoTheme;
  readonly excludeSrcs?: ReadonlySet<string> | readonly string[];
}): DirectoryMediaCandidate[] {
  const excluded = toExcludeSet(input.excludeSrcs);
  const theme = input.theme ?? 'light';
  const logoSrc = resolveThemeMediaUrl(input.logoUrl, input.logoUrlDark, theme, excluded);
  if (logoSrc !== null) {
    return [{ profile: 'brandMark', src: logoSrc }];
  }
  return [];
}

/**
 * Drop the failed primary URL from a parent-owned candidates list.
 * Returns a new array (immutable).
 */
export function dropFailedDirectoryCandidate(
  candidates: readonly DirectoryMediaCandidate[],
  failedSrc: string,
): DirectoryMediaCandidate[] {
  const trimmed = failedSrc.trim();
  if (trimmed.length === 0) {
    return [...candidates];
  }
  return candidates.filter((candidate) => candidate.src !== trimmed);
}
